"use client";

import React from "react";

import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  type SelectChangeEvent,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
} from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import type { RoomRowProps } from "../../../../../types/dashboard";
import { BASE_URL } from "../../../../api/config";
import { useUserContext } from "../../../../context/UserContext";
import ImageIcon from "@mui/icons-material/Image";
import type { Room } from "../../../../../types/context";
import CloseIcon from "@mui/icons-material/Close";
// import { useBookingContext } from "context/BookingContext"; // Not used currently
import UploadFileIcon from "@mui/icons-material/UploadFile";

const RoomRow: FC<RoomRowProps> = (props: RoomRowProps) => {
  const { room } = props;
  const { user } = useUserContext();
  
  // Debug logging
  console.log(`Room ${room.name}:`, {
    id: room.id,
    availabilities: room.room_availabilities,
    availabilityCount: room.room_availabilities?.length || 0
  });
  
  const [currentAvailability, setCurrentAvailability] = useState({
    date: room.room_availabilities?.[0]?.availability_id || '',
    availability: room.room_availabilities?.[0]?.available_rooms || 0,
  });
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  // const { state } = useBookingContext(); // Not used currently
  // const dispatch = useBookingContext().dispatch; // Not used currently

  // const [selectedValue, setSelectedValue] = useState<string>(''); // Not used currently
  // const [roomConfiguration, setRoomConfiguration] = useState<Array<string>>([]); // Not used currently
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // const selectedRooms = useMemo(() => {
  //   if (selectedValue && selectedValue !== '') {
  //     return state.rooms[Number(selectedValue)];
  //   }
  //   return undefined;
  // }, [selectedValue, state.rooms]); // Not used currently

  const dates = useMemo(() => {
    if (room.room_availabilities) {
      return room.room_availabilities.map((ra) => ra.availability_id);
    }
  }, [room.room_availabilities]);

  const handleCurrentAvailability = (event: SelectChangeEvent) => {
    if (room.room_availabilities) {
      const foundRoom = room.room_availabilities.find(
        (ra) => ra.availability_id === event.target.value
      );
      setCurrentAvailability((prev) => ({
        date: event.target.value,
        availability: foundRoom?.available_rooms || 0,
      }));
    }
  };

  const handleChangeCurrentAvailability = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(event.target.value);
    
    if (isNaN(value)) {
      return;
    }
    
    if (value < 0) {
      setQuantityError('No se permiten cantidades negativas');
      return;
    }
    
    setQuantityError(null);
    setCurrentAvailability((prev) => ({
      ...prev,
      availability: value,
    }));
  };

  // useEffect(() => {
  //   if (selectedRooms) {
  //     setRoomConfiguration(selectedRooms.map(() => "Matrimonial"));
  //   } else {
  //     setRoomConfiguration([]);
  //   }
  // }, [selectedRooms]); // Not used currently

  useEffect(() => {
    if (selectedRoom) {
      // const imagePath = `/images/${
      //   selectedRoom?.image || "habitacion-clasica.jpg"
      // }`; // Not used currently
      setImagePreview(null); // Reset preview when changing rooms
      setSelectedFile(null);
    }
  }, [selectedRoom]);

  const save = async () => {
    if (!user) {
      setNotification({
        open: true,
        message: "Usuario no autenticado",
        severity: "error",
      });
      return;
    }

    if (!currentAvailability.date || currentAvailability.availability === undefined) {
      setNotification({
        open: true,
        message: "Fecha y cantidad son requeridos",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);

    try {
      const requestData = {
        room_id: room.id,
        availability_id: currentAvailability.date,
        available_rooms: currentAvailability.availability,
      };
      
      console.log('Sending update request:', requestData);
      
      const response = await fetch(BASE_URL + "room-availabilities/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": user.token,
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();
      console.log('Server response:', { status: response.status, data });

      if (response.ok) {
        setIsEditing(false);
        setNotification({
          open: true,
          message: `Disponibilidad actualizada: ${currentAvailability.availability} habitaciones para ${currentAvailability.date}`,
          severity: "success",
        });
        
        // Update the local room data to reflect the change
        if (room.room_availabilities) {
          const updatedAvailability = room.room_availabilities.find(
            ra => ra.availability_id === currentAvailability.date
          );
          if (updatedAvailability) {
            updatedAvailability.available_rooms = currentAvailability.availability;
            console.log('Local data updated:', updatedAvailability);
          } else {
            console.warn('Could not find availability to update locally');
          }
        }
      } else {
        console.error('Server error response:', data);
        throw new Error(data.message || "Error al actualizar disponibilidad");
      }
    } catch (error) {
      console.error("Error saving availability:", error);
      setNotification({
        open: true,
        message: "Error al guardar: " + (error instanceof Error ? error.message : "Error desconocido"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: 800,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  const handleOpenModal = (room: Room) => {
    setSelectedRoom(room);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRoom(null);
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleRefreshImage = () => {
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        setNotification({
          open: true,
          message: "Solo se permiten archivos .jpg, .jpeg o .png",
          severity: "error",
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 200 * 1024) {
        setNotification({
          open: true,
          message: "El archivo es demasiado grande. Máximo 200kb permitido.",
          severity: "error",
        });
        return;
      }

      setIsLoading(true);
      setSelectedFile(file);

      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
        setIsLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveImage = async () => {
    if (!imagePreview || !selectedRoom) {
      setNotification({
        open: true,
        message: "No hay imagen para guardar",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Get the file extension from the selected file
      const fileExtension = selectedFile?.name.split(".").pop() || "jpg";

      // Send the base64 image data directly in the request body
      const response = await fetch(`${BASE_URL}room-availabilities/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": user?.token ?? "",
        },
        body: JSON.stringify({
          room_id: selectedRoom.id,
          image_data: imagePreview, // Send the base64 string directly
          file_extension: fileExtension,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setNotification({
          open: true,
          message: "Imagen guardada correctamente. Recargando página...",
          severity: "success",
        });

        // Clear the preview and selected file
        setImagePreview(null);
        setSelectedFile(null);

        // Close the modal
        setOpenModal(false);

        // Reload the page after a short delay to show the success message
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(data.message || "Error al guardar la imagen");
      }
    } catch (error) {
      console.error("Error saving image:", error);
      setNotification({
        open: true,
        message:
          "Error al guardar la imagen: " +
          (error instanceof Error ? error.message : "Error desconocido"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  return (
    <>
      <TableRow key={`${room.id}`}>
        <TableCell>{room.name}</TableCell>
        <TableCell>
          {dates && dates.length > 0 ? (
            <FormControl sx={{ m: 1, minWidth: 120 }} size='small'>
              <InputLabel id='demo-select-small'>Fecha</InputLabel>
              <Select
                labelId='demo-select-small'
                id='demo-select-small'
                value={currentAvailability.date}
                label='Fecha'
                onChange={handleCurrentAvailability}
              >
                {dates.map((date, index) => (
                  <MenuItem key={index} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin fechas disponibles
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ textAlign: "left" }}>
          <Box>
            <TextField
              id='outlined-number'
              label='Cantidad'
              type='number'
              size='small'
              value={currentAvailability.availability}
              onChange={handleChangeCurrentAvailability}
              error={!!quantityError}
              disabled={!dates || dates.length === 0}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{ min: 0 }}
            />
            {quantityError && (
              <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                {quantityError}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell width={200}>
          {isEditing ? (
            <Button 
              color='info' 
              variant='contained' 
              onClick={save}
              disabled={!dates || dates.length === 0}
            >
              Guardar
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => setIsEditing(true)}
                disabled={!dates || dates.length === 0}
              >
                Editar
              </Button>
              <IconButton
                color='primary'
                onClick={() => handleOpenModal(room)}
                size='small'
              >
                <ImageIcon />
              </IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby='room-image-modal'
        aria-describedby='modal-showing-room-image'
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography id='room-image-modal' variant='h6' component='h2'>
              {selectedRoom?.name || "Habitación"}
            </Typography>
            <IconButton onClick={handleCloseModal} size='small'>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedRoom ? (
            <Box
              sx={{ width: "100%", textAlign: "center", position: "relative" }}
            >
              {isLoading ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2 }}>Procesando imagen...</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ position: "relative" }}>
                    <img
                      src={`/images/${
                        (selectedRoom && selectedRoom?.image) ||
                        "habitacion-clasica.jpg"
                      }`}
                      alt={`Imagen de ${selectedRoom.name}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                        objectFit: "contain",
                      }}
                    />

                    {/* Botón de subir archivo en la parte inferior */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 2,
                      }}
                    >
                      <input
                        type='file'
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept='.jpg,.jpeg,.png'
                        style={{ display: "none" }}
                      />
                      <Button
                        variant='contained'
                        color='primary'
                        startIcon={<UploadFileIcon />}
                        onClick={handleRefreshImage}
                        disabled={isLoading}
                      >
                        Subir archivo
                      </Button>
                    </Box>

                    {/* Previsualización en miniatura en la esquina inferior derecha */}
                    {imagePreview && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 10,
                          right: 10,
                          zIndex: 10,
                        }}
                      >
                        <Paper elevation={3} sx={{ p: 1 }}>
                          <img
                            src={imagePreview || "/placeholder.svg"}
                            alt='Vista previa'
                            style={{
                              width: 100,
                              height: 100,
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                          <Button
                            variant='contained'
                            color='primary'
                            size='small'
                            onClick={saveImage}
                            disabled={isLoading}
                            fullWidth
                            sx={{ mt: 1 }}
                          >
                            Guardar
                          </Button>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ textAlign: "center" }}
            >
              No hay imagen disponible para esta habitación.
            </Typography>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RoomRow;
